import { BehaviorSubject, filter, map, Observable, withLatestFrom } from 'rxjs';
import { getGameResultType } from '@golf-ar/core';
import { Scene } from '@babylonjs/core';

import { GolfBall } from './golf-ball';
import { Golfer } from './golfer';
import { Field } from './field';

const GAME_MANAGER_DEFAULTS = {
	strokes: 0,
	isEndGame: false,
	isBallInHole: false,
	isLoading: false,
};

/** Game manager. */
export class GameManager {

	/** Whether game is end. */
	public readonly isEndGame$ = new BehaviorSubject(GAME_MANAGER_DEFAULTS.isEndGame);

	/** Status of a ball in a hole. */
	public readonly isBallInHole$ = new BehaviorSubject(GAME_MANAGER_DEFAULTS.isBallInHole);

	/** End game strokes. */
	public readonly endGameStrokes$: Observable<number>;

	/** Whether scene is loading or not. */
	public readonly isLoading$ = new BehaviorSubject(GAME_MANAGER_DEFAULTS.isLoading);

	/** End strokes count but at it's end. */
	private readonly endStrokesCount$ = new BehaviorSubject(GAME_MANAGER_DEFAULTS.strokes);

	public constructor(
		private readonly golfBall: GolfBall,
		private readonly golfer: Golfer,
		private readonly field: Field,
		private readonly scene: Scene,
		private readonly cameraReset: () => void,
		private readonly groundReset: () => void,
	) {
		this.endGameStrokes$ = this.endStrokesCount$.pipe(
			withLatestFrom(this.isEndGame$),
			filter(([strokesCount, isEndGame]) => getGameResultType(strokesCount) === 'miss' || isEndGame),
			map(([strokesCount]) => strokesCount),
		);
	}

	/** Resets the game. */
	public resetGame(): void {
		this.golfBall.setPosition(this.field.startPosition, true);
		this.golfer.changeGolferPosition();
		this.golfer.instanceAnimation.reset();
		this.isEndGame$.next(GAME_MANAGER_DEFAULTS.isEndGame);
		this.isBallInHole$.next(GAME_MANAGER_DEFAULTS.isBallInHole);
		this.endStrokesCount$.next(GAME_MANAGER_DEFAULTS.strokes);
		this.cameraReset();
		this.groundReset();

		// We need to re-render the scene to make sure
		// physics pre-step is enabled for next manipulations.
		this.scene.render();
	}

	/** Adds strokes count but at it's end. */
	public addEndStroke(): void {
		this.endStrokesCount$.next(this.endStrokesCount$.value + 1);
	}

	/** Whether the game over. */
	public getIsEndGame(): boolean {
		return this.isEndGame$.value;
	}

	/**
	 * Sets status of a ball in a hole.
	 * @param value Value.
	 */
	public setIsBallInHole(value: boolean): void {
		this.isBallInHole$.next(value);
	}
}
